import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Spin,
  Row,
  Typography,
  Form,
  Col,
  Button,
  Input,
} from 'antd';
import { CloseCircleOutlined, PlusCircleOutlined, SearchOutlined } from '@ant-design/icons';
import Table from 'components/Table';
import { getTipoDeConceptoDeInfracciones } from 'api/recaudacion/tipoDeConceptosDeInfraccion';
import { deleteConceptoDeInfraccion, getConceptosDeInfracciones } from 'api/recaudacion/conceptosDeInfracciones';
import toCurrency from 'utils/toCurrency';
import { getFundamentosLegales } from 'api/configuracion/fundamentosLegales';
import { getMonedas } from 'api/configuracion';
import { patchInfraccion } from 'api/recaudacion/infracciones';
import { useSelector } from 'react-redux';
import ModalAgregarConceptos from './ModalAgregarConceptos';
import Select from './Select';

const TableTipoDeConceptos = ({
  infraccion,
  permission,
  changeStatus,
  soloConsulta,
}) => {
  const [form] = Form.useForm();
  const periodoFiscalSelected = useSelector(({ auth }) => auth.periodoFiscalSelected);
  const [data, setData] = useState([]);
  const [fundamentos, setFundamentos] = useState([]);
  const [monedas, setMonedas] = useState([]);
  const [dataConceptos, setDataConceptos] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRowKeysConceptos, setSelectedRowKeysConceptos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visibleModalCaptura, setVisibleModalCaptura] = useState(false);
  const [disabledCancelar, setDisabledCancelar] = useState(true);
  const [disabledAsignar, setDisabledAsignar] = useState(true);

  useEffect(() => {
    if (soloConsulta) {
      setDisabledCancelar(true);
      setDisabledAsignar(true);
    }
  }, [selectedRowKeysConceptos, dataConceptos, soloConsulta]);

  const fundamentoToString = (e) => `${e?.denominacion || ''} -  Artículo: ${e?.articulo || ''} - Fracción: ${e?.fraccion || ''} `;

  const fetchAssinged = async (_changeStatus = false) => {
    if (infraccion) {
      setLoading(true);
      const _data = await getConceptosDeInfracciones({ infraccion });

      const haveConceptos = _data.some((c) => c.concepto_activo);
      if (!haveConceptos && _changeStatus) {
        await patchInfraccion(infraccion, { estado_de_la_infraccion: 2 });
        changeStatus(2);
      }
      const _conceptos = _data.filter((c) => c.concepto_activo);
      setDataConceptos(_conceptos);
      setLoading(false);
    }
  };

  const fetch = async () => {
    setLoading(true);
    const values = form.getFieldsValue();
    const fundamento_legal = form.getFieldValue('fundamento_legal');
    // Solo se obtienen los tipos de conceptos que esten configurados como visibles
    // Y tambien los que son del fundamento legal seleccionado en el formulario de busqueda
    const _data = await getTipoDeConceptoDeInfracciones({
      fundamento_legal,
      es_visible: true,
      descripcion_corta: values.descripcion || undefined,
      clave: values.clave || undefined,
      periodo_fiscal: periodoFiscalSelected?.id,
    });
    setData(_data);
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const _fetchData = async () => {
      setLoading(true);
      const [_fundamentos, _monedas] = await Promise.all([
        getFundamentosLegales({ es_para_infracciones: true }),
        getMonedas(),
      ]);
      if (mounted) {
        setFundamentos(_fundamentos.map((e) => ({
          ...e,
          denominacion: fundamentoToString(e),
        })));
        setMonedas(_monedas);
        setLoading(false);
      }
    };
    fetchAssinged();
    _fetchData();
    return () => { mounted = false; };
  }, []);

  const handleOnRowClick = (record) => setSelectedRowKeys([record.id]);

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
    getCheckboxProps: (r) => {
      if (!r.es_concepto_capturable) return { disabled: true };
      const count = dataConceptos.filter((c) => c.tipo_de_concepto_de_infraccion === r.id).length;
      const disabled = count >= (r.es_concepto_repetible ? r.maximo_de_concepto_repetido : 1);
      return { disabled };
    },
  };

  const handleOnRowClickConceptos = (record) => {
    if (soloConsulta) {
      setDisabledCancelar(true);
      return;
    }
    setDisabledCancelar(false);
    setSelectedRowKeysConceptos([record.id]);
  };

  const rowSelectionConceptos = {
    selectedRowKeys: selectedRowKeysConceptos,
    type: 'radio',
  };

  const columns = [
    {
      titleText: 'Clave',
      dataIndex: 'clave',
      key: 'clave',
      width: 100,
    },
    {
      titleText: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
      width: 200,
    },
  ];

  const columnsAssigned = [
    {
      titleText: 'Clave',
      dataIndex: 'clave_de_tipo_de_concepto',
      key: 'clave_de_tipo_de_concepto',
      width: 100,
    },
    {
      titleText: 'Descripción',
      dataIndex: 'descripcion_de_tipo_de_concepto',
      key: 'descripcion_de_tipo_de_concepto',
      width: 300,
    },
    {
      titleText: 'Importe',
      dataIndex: 'importe',
      key: 'importe',
      width: 100,
      render: (val, record) => {
        if (record.moneda === 1) return toCurrency(val);
        return `${val} ${monedas.find((m) => m.id === record.moneda)?.clave}`;
      },
    },
  ];

  const assignConceptos = async () => {
    setVisibleModalCaptura(true);
  };

  const deleteConcepto = async () => {
    setLoading(true);
    const [key] = selectedRowKeysConceptos;

    if (key) {
      const deleted = await deleteConceptoDeInfraccion({
        infraccionId: infraccion,
        conceptos: key,
      });
      if (deleted) {
        setSelectedRowKeysConceptos([]);
        setDataConceptos([]);
        setSelectedRowKeys([]);
        await fetchAssinged(true);
      }
    }
    setLoading(false);
  };

  return (
    <>
      <Spin tip="Cargando..." spinning={loading}>
        <Typography.Title level={5}>
          Buscar por:
        </Typography.Title>
        <Col span={24} />
        <Form
          layout="vertical"
          form={form}
          scrollToFirstError
        >
          <Row gutter={10}>
            <Col xs={24} sm={24} md={8}>
              <Form.Item
                name="clave"
                label="Clave"
                hasFeedback
              >
                <Input allowClear disabled={soloConsulta} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={8}>
              <Form.Item
                name="fundamento_legal"
                label="Fundamento legal"
                hasFeedback
              >
                <Select
                  disabled={soloConsulta}
                  dataSource={fundamentos}
                  labelProp="denominacion"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={8}>
              <Form.Item
                name="descripcion"
                label="Descripcion"
                hasFeedback
              >
                <Input allowClear disabled={soloConsulta} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={8}>
              <Button
                onClick={fetch}
                block
                style={{ marginTop: 21 }}
                disabled={soloConsulta}
              >
                <SearchOutlined />
                <Typography.Text>
                  Buscar
                </Typography.Text>
              </Button>
            </Col>
          </Row>
        </Form>
        <Col span={24} />
        <Row gutter={10}>
          <Typography.Title level={5}>
            Conceptos disponibles
          </Typography.Title>
          <Table
            cols={columns}
            data={data}
            rowSelection={rowSelection}
            handleOnRowClick={handleOnRowClick}
            rowKey="id"
            permission={permission}
            allowSearch={false}
            allowColumnSelector={false}
            hasAllViewPermission
            mobileColIndex={0}
            customActions={[
              {
                onClick: async () => assignConceptos(),
                icon: <PlusCircleOutlined />,
                text: 'Asignar conceptos',
                disabledBySelection: disabledAsignar,
              },
            ]}
          />
        </Row>
        <br />
        <br />
        <br />
        <Typography.Title level={5}>
          Conceptos asignados
        </Typography.Title>
        <Table
          cols={columnsAssigned}
          data={dataConceptos}
          rowSelection={rowSelectionConceptos}
          handleOnRowClick={handleOnRowClickConceptos}
          rowKey="id"
          permission={permission}
          hasAllViewPermission
          mobileColIndex={0}
          allowSearch={false}
          allowColumnSelector={false}
          customActions={[
            {
              onClick: () => deleteConcepto(),
              icon: <CloseCircleOutlined />,
              text: 'Cancelar Concepto',
              disabledBySelection: disabledCancelar,
            },
          ]}
        />
      </Spin>
      <ModalAgregarConceptos
        visible={visibleModalCaptura}
        selectedConcepto={selectedRowKeys[0]}
        onCancel={() => {
          setVisibleModalCaptura(false);
          setSelectedRowKeys([]);
        }}
        infraccion={infraccion}
        callback={fetchAssinged}
        changeStatus={changeStatus}
        existentes={dataConceptos
          .filter((c) => c.tipo_de_concepto_de_infraccion === selectedRowKeys[0]).length}
      />
    </>
  );
};

TableTipoDeConceptos.propTypes = {
  infraccion: PropTypes.number,
  permission: PropTypes.shape({
    add: PropTypes.bool.isRequired,
    change: PropTypes.bool.isRequired,
    delete: PropTypes.bool.isRequired,
    export: PropTypes.bool.isRequired,
    import: PropTypes.bool.isRequired,
    view: PropTypes.bool.isRequired,
  }).isRequired,
  changeStatus: PropTypes.func,
  soloConsulta: PropTypes.bool,
};

TableTipoDeConceptos.defaultProps = {
  infraccion: null,
  changeStatus: () => {},
  soloConsulta: false,
};

export default TableTipoDeConceptos;
